import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { LEGAL } from "../legal/legalConfig";
import { openCookieSettings } from "../legal/CookieBanner";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import { myInvites, respondInvite, errText } from "../lib/teamInvites";
import { watchSubscriptionStatus } from "../lib/teamCheckout";
import axios from "axios";
import { CompanyPolicyLinks } from "../legal/LegalFooterLinks";
import { RequestRefundButton, MyRefundRequests } from "../components/RefundRequests";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

/* Your plan: the Team subscription (renews monthly via Razorpay until cancelled) with next charge, grace
   period and a cancel button. Cancelling stops renewal; Team stays until the end of the paid month. */
export function YourPlanSection({ plan, subs, onCancel, busy }) {
  const live = (subs || []).filter((s) => ["active", "authenticated", "pending", "halted", "cancel_scheduled"].includes(s.status));
  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="settings-plan">
      <h2 className="font-display text-sm font-semibold">Your plan</h2>
      <p className="mt-2 text-sm" data-testid="settings-plan-name">
        {plan?.plan === "team" ? "Team" : "Free"}
        {plan ? ` · ${plan.runs_used_30d} of ${Number(plan.runs_quota_30d).toLocaleString("en-IN")} runs used in the last 30 days` : ""}
        {plan?.run_credits ? ` · ${plan.run_credits} extra run${plan.run_credits === 1 ? "" : "s"} (used after the allowance)` : ""}
      </p>
      {plan?.run_model?.fixed && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="settings-plan-model">Team runs use {plan.run_model.id === "gemini-2.5-flash-lite" ? "Gemini 2.5 Flash-Lite" : plan.run_model.id}.</p>
      )}
      {live.map((s) => (
        <div key={s.id} className="mt-3 rounded-xl border border-border p-3 text-sm" data-testid={`settings-sub-${s.id}`}>
          <p>Team · ₹{Number(s.amount_inr).toLocaleString("en-IN")} a month ({LEGAL.priceNote}) · UPI Autopay or card</p>
          {s.status === "cancel_scheduled" ? (
            <p className="mt-1 text-xs text-muted-foreground">Your plan ends on {fmtDate(s.entitled_until)}. It won't renew.</p>
          ) : ["pending", "halted"].includes(s.status) ? (
            <p className="mt-1 text-xs text-amber-700" data-testid="settings-sub-grace">Your last payment didn't go through. Team stays on until {fmtDate(s.grace_until)}, then your account moves to Free.</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">Renews on {fmtDate(s.next_charge_at || s.entitled_until)}. We'll email you before each charge.</p>
          )}
          {s.status !== "cancel_scheduled" && (
            <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => onCancel(s.id)} data-testid="settings-sub-cancel">Cancel renewal</Button>
          )}
          {s.status === "cancel_scheduled" && (
            <div className="mt-2"><RequestRefundButton kind="subscription" refId={s.id} className="rounded-md border border-border px-3 py-1 text-xs" /></div>
          )}
        </div>
      ))}
      <MyRefundRequests reloadKey={(subs || []).length} />
      {!live.length && plan?.plan !== "team" && (
        <p className="mt-2 text-xs text-muted-foreground">Upgrade on the <Link className="underline" to="/pricing">Pricing page</Link>.</p>
      )}
    </section>
  );
}

const LEGAL_ITEMS = [
  { key: "terms", label: "Terms of Service", to: LEGAL.routes.terms },
  { key: "privacy", label: "Privacy Policy", to: LEGAL.routes.privacy },
  { key: "cookies", label: "Cookie Policy", to: LEGAL.routes.cookies },
  { key: "refund", label: "Refund & Cancellation Policy", to: LEGAL.routes.refund },
  { key: "pricing", label: `Pricing (INR ${LEGAL.priceNote})`, to: LEGAL.routes.pricing },
];

/* Delete account (store requirement): type DELETE + password; Google/Emergent accounts must have signed in recently. */
export function DeleteAccountSection({ user, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const needsPassword = user?.has_password !== false;
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/auth/delete-account`, { confirm, password: needsPassword ? password : undefined });
      onDeleted();
    } catch (e) {
      setErr(errText(e, "Couldn't delete your account. Please try again or email us."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-6 rounded-2xl border border-red-300 bg-card p-5" data-testid="settings-delete">
      <h2 className="font-display text-sm font-semibold">Delete account</h2>
      <p className="mt-2 text-xs text-muted-foreground">
        This permanently deletes your account, runs, files, skills and team memberships, cancels any active plan straight
        away and signs you out everywhere. We keep only records the law requires (such as invoices), without your name or email.
      </p>
      {!open ? (
        <Button size="sm" variant="outline" className="mt-3 text-red-600" onClick={() => setOpen(true)} data-testid="settings-delete-open">Delete account</Button>
      ) : (
        <div className="mt-3 space-y-2">
          <input className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Type DELETE to confirm"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="settings-delete-confirm" />
          {needsPassword ? (
            <input type="password" autoComplete="current-password" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="settings-delete-password" />
          ) : (
            <p className="text-xs text-muted-foreground">You sign in with Google: if you signed in more than 10 minutes ago, sign out and sign in again first.</p>
          )}
          {err && <p className="text-xs text-red-600" data-testid="settings-delete-error">{err}</p>}
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={busy || confirm.trim().toUpperCase() !== "DELETE" || (needsPassword && !password)}
              onClick={submit} data-testid="settings-delete-submit">{busy ? "Deleting…" : "Delete my account"}</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => { setOpen(false); setErr(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function Settings() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState({ invites: [], needs_verification: false });
  const [note, setNote] = useState("");

  const [plan, setPlan] = useState(null);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadPlan = () => {
    axios.get(`${API}/plan`).then((r) => setPlan(r.data)).catch(() => {});
    axios.get(`${API}/subscriptions/mine`).then((r) => setSubs(r.data.subscriptions || [])).catch(() => {});
  };
  const cancelSub = async (id) => {
    if (!window.confirm("Cancel renewal? Team stays on until the end of the month you've paid for.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscriptions/${id}/cancel`);
      setNote("Renewal cancelled.");
      loadPlan();
    } catch (e) {
      setNote(errText(e, "Couldn't cancel. Please try again or email us."));
    } finally {
      setBusy(false);
    }
  };
  const load = () => { loadPlan(); return myInvites().then(setInvites).catch(() => {}); };
  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!user) return undefined;
    const stop = watchSubscriptionStatus({ onActive: () => loadPlan() });
    load();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const respond = async (id, accept) => {
    try {
      const r = await respondInvite(id, accept);
      setNote(accept ? `You joined ${r.team_name}.` : "Invitation declined.");
      load();
    } catch (e) {
      setNote(errText(e, "Couldn't update the invitation."));
    }
  };

  if (!user) return null;
  const row = "flex items-center justify-between py-3 text-sm hover:text-primary";
  return (
    <div className="mx-auto max-w-2xl px-5 py-10" data-testid="settings-page">
      <Link to="/workspace" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Workspace</Link>
      <h1 className="mt-4 font-display text-3xl font-bold">Settings</h1>

      <section className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="settings-account">
        <h2 className="font-display text-sm font-semibold">Account</h2>
        <p className="mt-2 text-sm">{user.name} · {user.email}</p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="settings-email-status">
          Email {user.email_verified === false ? "not confirmed yet" : "confirmed"}
        </p>
        <div className="mt-3"><VerifyEmailBanner user={user} onToast={setNote} /></div>
        <Button variant="ghost" className="mt-3 px-0 text-sm text-muted-foreground" onClick={() => { logout(); navigate("/"); }}>Sign out</Button>
      </section>

      <YourPlanSection plan={plan} subs={subs} onCancel={cancelSub} busy={busy} />

      <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="settings-invites">
        <h2 className="font-display text-sm font-semibold">Team invitations</h2>
        {invites.needs_verification ? (
          <p className="mt-2 text-sm text-muted-foreground">Confirm your email to see invitations sent to it (or use the link in the invite email).</p>
        ) : invites.invites.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No pending invitations.</p>
        ) : invites.invites.map((inv) => (
          <div key={inv.id} className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3" data-testid={`settings-invite-${inv.id}`}>
            <span className="text-sm"><strong>{inv.team_name}</strong>{inv.inviter_name ? ` · from ${inv.inviter_name}` : ""}</span>
            <span className="flex gap-2">
              <Button size="sm" onClick={() => respond(inv.id, true)}>Accept</Button>
              <Button size="sm" variant="outline" onClick={() => respond(inv.id, false)}>Decline</Button>
            </span>
          </div>
        ))}
        {note && <p className="mt-3 text-xs text-muted-foreground" data-testid="settings-note">{note}</p>}
      </section>

      <DeleteAccountSection user={user} onDeleted={() => { logout(); navigate("/"); }} />

      <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="settings-legal">
        <h2 className="font-display text-sm font-semibold">Legal &amp; billing</h2>
        <ul className="mt-2 divide-y divide-border">
          {LEGAL_ITEMS.map((l) => (
            <li key={l.key}>
              <Link to={l.to} className={row} data-testid={`settings-link-${l.key}`}>{l.label}<ChevronRight className="h-4 w-4" /></Link>
            </li>
          ))}
          <li>
            <Link to="/help" className={row} data-testid="settings-link-help">User guide &amp; FAQ<ChevronRight className="h-4 w-4" /></Link>
            <Link to="/support" className={row} data-testid="settings-link-support">Support tickets<ChevronRight className="h-4 w-4" /></Link>
          </li>
          {user.staff_role && (
            <li>
              <Link to="/admin" className={row} data-testid="settings-link-admin">Staff console<ChevronRight className="h-4 w-4" /></Link>
            </li>
          )}
          <li>
            <button type="button" onClick={openCookieSettings} className={`${row} w-full text-left`} data-testid="settings-cookie-settings">Cookie settings</button>
          </li>
        </ul>
        <p className="mt-4 text-xs text-muted-foreground" data-testid="settings-grievance">
          Grievance Officer: Suhas Raju, <a className="underline" href={`mailto:${LEGAL.contactEmail}`}>info@headkeysoftwaresolution.com</a>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{LEGAL.companyName} · CIN {LEGAL.companyCin}</p>
        <div className="mt-4" data-testid="settings-company-policies">
          <CompanyPolicyLinks className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" linkClassName="underline hover:text-primary" />
        </div>
      </section>
    </div>
  );
}
